import React, { useState, useRef, useEffect } from 'react';
import { formatTimeOfDay } from '../utils/dateUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, X, Volume2, VolumeX, Move, Mic, MicOff, MessageCircle, Trash2 } from 'lucide-react';

interface Message {
  id: string;
  type: 'user' | 'joe';
  content: string;
  timestamp: Date;
}

interface JoeLargeAvatarProps {
  initialPosition?: 'bottom-right' | 'bottom-left' | 'bottom-center';
}

// Cache version - increment to clear old/corrupted cached messages
// v8: Fixed #27 entity decoding for TTS
// v9: Reset joe_position - the old default stored Joe off the bottom of the screen
const JOE_LARGE_CACHE_VERSION = '9';

// Expanded avatar is w-56 h-72 (224x288); the minimized circle is w-20 h-20 (80px).
// The minimized circle is drawn at position + COLLAPSED_OFFSET, so `position` is
// chosen to land that circle in the bottom-right corner - minimized is the
// default state (isOpen starts false), so it is what anchors the corner.
const AVATAR_W = 224;
const AVATAR_H = 288;
const CIRCLE_SIZE = 80;
const EDGE_MARGIN = 20;
const COLLAPSED_OFFSET = { x: 68, y: 220 };

// Decode HTML entities that may have been added by input sanitizer
// Fixes "hashtag 27" bug where &#x27; (apostrophe entity) gets read aloud
const decodeHtmlEntities = (text: string): string => {
  if (!text) return text;
  return text
    // Handle full HTML entities
    .replace(/&#x27;/gi, "'")
    .replace(/&#39;/gi, "'")
    .replace(/&#27;/gi, "'")  // Some systems use #27 without x
    .replace(/&quot;/gi, '"')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    // Handle partial entities where & was stripped (fixes "hashtag 27" TTS bug)
    .replace(/#x27;?/gi, "'")
    .replace(/#39;?/gi, "'")
    .replace(/#27;?/gi, "'")   // Plain #27 without x
    .replace(/x27;?/gi, "'")   // Just x27 without #
    // Handle spaces that can appear in corrupted entities
    .replace(/&\s*#\s*x?\s*27\s*;?/gi, "'")
    .replace(/&\s*#\s*39\s*;?/gi, "'");
};

export const JoeLargeAvatar: React.FC<JoeLargeAvatarProps> = ({
  initialPosition = 'bottom-right'
}) => {
  const navigate = useNavigate();
  const location = useLocation(); // For context-aware commands

  // Ref for navigate to avoid stale closure in callbacks
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Load persisted states from localStorage
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem('joe_isOpen');
    return saved ? JSON.parse(saved) : false; // Default to minimized
  });

  const [isMuted, setIsMuted] = useState(() => {
    const saved = localStorage.getItem('joe_isMuted');
    return saved ? JSON.parse(saved) : false;
  });

  const [showChat, setShowChat] = useState(() => {
    const saved = localStorage.getItem('joe_showChat');
    return saved ? JSON.parse(saved) : true;
  });

  // Initialize messages from localStorage to prevent reset on navigation
  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const cachedVersion = localStorage.getItem('joe_large_version');
      if (cachedVersion !== JOE_LARGE_CACHE_VERSION) return [];
      const raw = localStorage.getItem('joe_messages');
      if (raw) {
        const parsed = JSON.parse(raw) as Array<{ id: string; type: 'user'|'joe'; content: string; timestamp: string }>;
        return parsed.map(m => ({
          ...m,
          content: decodeHtmlEntities(m.content),
          timestamp: new Date(m.timestamp)
        }));
      }
    } catch {}
    return [];
  });
  const [input, setInput] = useState<string>(() => {
    try {
      return localStorage.getItem('joe_input') || '';
    } catch {
      return '';
    }
  });
  const [isTyping, setIsTyping] = useState(false);
  type AnimationState = 'idle' | 'thinking' | 'talking' | 'listening' | 'celebrating';
  const [animationState, setAnimationState] = useState<AnimationState>('idle');
  const isTalking = animationState === 'talking';
  const [currentFrame, setCurrentFrame] = useState(1);
  // Load persisted position from localStorage or set sensible default
  // Bottom-right corner, with the MINIMIZED circle sitting EDGE_MARGIN from
  // each edge. The old default (innerWidth-100, innerHeight-120) put the circle
  // at innerHeight+100 - entirely below the fold, so Joe was invisible on load.
  const getDefaultPosition = () => ({
    x: Math.max(0, window.innerWidth - CIRCLE_SIZE - EDGE_MARGIN - COLLAPSED_OFFSET.x),
    y: Math.max(0, window.innerHeight - CIRCLE_SIZE - EDGE_MARGIN - COLLAPSED_OFFSET.y)
  });

  // Keeps the 80px minimized circle on screen.
  const clampPosition = (pos: { x: number; y: number }) => ({
    x: Math.max(0, Math.min(pos.x, window.innerWidth - CIRCLE_SIZE)),
    y: Math.max(0, Math.min(pos.y, window.innerHeight - CIRCLE_SIZE))
  });

  // Keeps the full 224x288 expanded avatar on screen.
  const clampExpanded = (pos: { x: number; y: number }) => ({
    x: Math.max(0, Math.min(pos.x, window.innerWidth - AVATAR_W - EDGE_MARGIN)),
    y: Math.max(0, Math.min(pos.y, window.innerHeight - AVATAR_H - EDGE_MARGIN))
  });

  const [position, setPosition] = useState(() => {
    try {
      // Every browser that ever loaded the app persisted the old broken default
      // on mount, so a new default alone would never reach anyone. Ignore the
      // saved position once, on the version bump, the same way messages do above.
      const cachedVersion = localStorage.getItem('joe_large_version');
      const saved = cachedVersion === JOE_LARGE_CACHE_VERSION
        ? localStorage.getItem('joe_position')
        : null;
      if (saved) {
        const parsed = JSON.parse(saved);
        return clampPosition({ x: parsed.x || 0, y: parsed.y || 0 });
      }
    } catch {}
    return getDefaultPosition();
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [positionInitialized, setPositionInitialized] = useState(true);

  // Re-clamp position when viewport changes (orientation, resize)
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => clampPosition(prev));
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  const [isListening, setIsListening] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);

  const joeRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recognitionRef = useRef<any>(null);

  // Ref to avoid stale closure for mute state in speech recognition callback
  const isMutedRef = useRef(isMuted);

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Animation frame sequences for each state — uses frames 1-49, 51-56 (skip 50)
  const animationConfigs = {
    idle: {
      frames: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2],
      frameDelay: 180,
    },
    thinking: {
      frames: [43, 44, 45, 46, 47, 48, 49, 48, 47, 46, 45, 44, 43, 42, 41, 42, 43, 44, 45, 46],
      frameDelay: 140,
    },
    talking: {
      frames: Array.from({ length: 49 }, (_, i) => i + 1),  // frames 1-49 for fuller talking
      frameDelay: 75,
    },
    listening: {
      frames: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 17, 16, 15, 14, 13, 12, 11, 10, 9],
      frameDelay: 160,
    },
    celebrating: {
      frames: [51, 52, 53, 54, 55, 56, 55, 54, 53, 52, 51, 49, 48, 49, 51, 52, 53, 54, 55, 56],
      frameDelay: 90,
    },
  };
  // Idle fidgets — played occasionally to make Joe feel alive
  const idleFidgets = [
    [11, 12, 13, 14, 15, 16, 15, 14, 13, 12, 11],       // head tilt
    [15, 16, 17, 18, 19, 20, 19, 18, 17, 16, 15],       // slight movement
    [20, 21, 22, 23, 24, 23, 22, 21, 20],                // blink/nod
    [25, 26, 27, 28, 29, 28, 27, 26, 25],                // expression change
    [30, 31, 32, 33, 34, 33, 32, 31, 30],                // larger movement
  ];
  const totalFrames = 56;

  // Normalize common voice-to-text misinterpretations into intents
  const normalizeVoiceInput = (text: string) => {
    let t = (text || '').trim();
    const lower = t.toLowerCase();
    // Common mishears for "create"
    if (/\b(greater|grater|crate|creek|credit)\b/.test(lower)) {
      t = t.replace(/\b(greater|grater|crate|creek|credit)\b/gi, 'create');
    }
    // Normalize schedule phrases
    t = t.replace(/\bto day\b/gi, 'today').replace(/\bto morrow\b/gi, 'tomorrow');
    // Normalize punctuation spacing
    return t.replace(/\s+/g, ' ').trim();
  };

  // Get frame path
  const getFramePath = (frameNum: number) => {
    return `/joe/PBP Avatar_${frameNum}.png`;
  };

  // No need to load voices - using Amazon Polly backend

  // Clear old cached messages if version changed (fixes corrupted greeting issues)
  useEffect(() => {
    const cachedVersion = localStorage.getItem('joe_large_version');
    if (cachedVersion !== JOE_LARGE_CACHE_VERSION) {
      localStorage.removeItem('joe_messages');
      localStorage.removeItem('joe_hasGreeted');
      localStorage.setItem('joe_large_version', JOE_LARGE_CACHE_VERSION);
    }
  }, []);

  // Note: Messages are now restored in useState initializer to prevent race conditions

  // Persist messages, input, and UI toggles
  useEffect(() => {
    try {
      const serializable = messages.map(m => ({ ...m, timestamp: new Date(m.timestamp).toISOString() }));
      localStorage.setItem('joe_messages', JSON.stringify(serializable));
    } catch {}
  }, [messages]);

  useEffect(() => {
    try { localStorage.setItem('joe_input', input); } catch {}
  }, [input]);

  useEffect(() => {
    try { localStorage.setItem('joe_isOpen', JSON.stringify(isOpen)); } catch {}
  }, [isOpen, messages.length]);

  useEffect(() => {
    try { localStorage.setItem('joe_isMuted', JSON.stringify(isMuted)); } catch {}
  }, [isMuted]);

  useEffect(() => {
    try { localStorage.setItem('joe_showChat', JSON.stringify(showChat)); } catch {}
  }, [showChat]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        console.log('🎤 Listening started...');
        setIsListening(true);
        setAnimationState('listening');
      };

      recognition.onresult = (event: any) => {
        const raw = event.results[0][0].transcript;
        const transcript = normalizeVoiceInput(raw);
        console.log('🎤 Heard:', raw, '| Normalized:', transcript);

        if (transcript.trim()) {
          // Check for stop command - stop Joe without sending to backend
          const stopPhrases = ['stop', 'shut up', 'quiet', 'be quiet', 'hush', 'enough', 'ok stop', 'okay stop', 'stop talking', 'stop it', 'shush', 'silence'];
          const lowerTranscript = transcript.toLowerCase().trim();
          const isStop = stopPhrases.some(phrase => lowerTranscript === phrase || lowerTranscript.startsWith(phrase + ' ') || lowerTranscript.endsWith(' ' + phrase));

          if (isStop) {
            // Stop speaking immediately
            if (audioRef.current) {
              audioRef.current.pause();
              audioRef.current.currentTime = 0;
              audioRef.current = null;
            }
            setAnimationState('idle');
            const joeMessage: Message = {
              id: Date.now().toString(),
              type: 'joe',
              content: "Okay, I will be quiet.",
              timestamp: new Date()
            };
            setMessages(prev => [...prev, joeMessage]);
            return;
          }

          // Auto-submit the voice input
          const userMessage: Message = {
            id: Date.now().toString(),
            type: 'user',
            content: transcript,
            timestamp: new Date()
          };

          setMessages(prev => [...prev, userMessage]);
          setIsTyping(true);
          setAnimationState('thinking');

          // Get current path at time of speech (avoid stale closure)
          const currentPath = window.location.pathname;

          // Send to Joe
          fetch('/api/joe/chat', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ message: transcript, currentPath })
          })
          .then(response => response.json())
          .then(data => {
            // Handle navigation directive but speak before routing
            let navTo: string | null = null;
            let cleaned = (data.response || '').replace(/\[\[NAV:[^\]]+\]\]\s*/,'').trim();
            // Decode HTML entities that may have been added by input sanitizer
            cleaned = decodeHtmlEntities(cleaned);
            if (data && typeof data.response === 'string') {
              const nav = data.response.match(/\[\[NAV:([^\]]+)\]\]/);
              if (nav && nav[1]) navTo = nav[1];
            }
            const joeMessage: Message = {
              id: (Date.now() + 1).toString(),
              type: 'joe',
              content: cleaned,
              timestamp: new Date()
            };
            setMessages(prev => [...prev, joeMessage]);
            // Use speakText from response if available (for walkthroughs), otherwise use cleaned text
            // Speak slower for walkthroughs (data.source === 'walkthrough')
            const textToSpeak = data.speakText || cleaned;
            const isWalkthrough = data.source === 'walkthrough';
            speakText(textToSpeak, isWalkthrough);
            if (navTo) {
              // Celebrate briefly then navigate
              setAnimationState('celebrating');
              const destination = navTo;
              setTimeout(() => {
                navigateRef.current(destination);
                setAnimationState('idle');
              }, 800);
            }
          })
          .catch(error => {
            console.error('Chat error:', error);
            setAnimationState('idle');
            const errorMessage: Message = {
              id: (Date.now() + 1).toString(),
              type: 'joe',
              content: "Sorry, I am having trouble connecting right now. Please try again.",
              timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
          })
          .finally(() => {
            setIsTyping(false);
          });
        }
      };

      recognition.onerror = (event: any) => {
        console.error('🎤 Recognition error:', event.error);
        setIsListening(false);
        setAnimationState(prev => prev === 'listening' ? 'idle' : prev);

        // Provide user-friendly error messages
        let errorMsg = '';
        switch (event.error) {
          case 'audio-capture':
            errorMsg = 'Microphone not detected or in use by another app. Please check your microphone.';
            break;
          case 'not-allowed':
            errorMsg = 'Microphone access denied. Please allow microphone access in your browser settings.';
            break;
          case 'network':
            errorMsg = 'Network error. Please check your internet connection.';
            break;
          case 'no-speech':
            errorMsg = 'No speech detected. Please try again.';
            break;
          case 'aborted':
            // User or system stopped - don't show error
            break;
          default:
            errorMsg = `Voice input error: ${event.error}`;
        }

        if (errorMsg) {
          setMicError(errorMsg);
          // Clear error after 5 seconds
          setTimeout(() => setMicError(null), 5000);
        }
      };

      recognition.onend = () => {
        console.log('🎤 Listening stopped');
        setIsListening(false);
        // Only return to idle if we're not already processing (thinking/talking)
        setAnimationState(prev => prev === 'listening' ? 'idle' : prev);
      };

      recognitionRef.current = recognition;
    } else {
      console.warn('⚠️ Speech recognition not supported in this browser');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount: stop audio and recognition
  useEffect(() => {
    return () => {
      try { stopSpeaking(); } catch {}
      try { recognitionRef.current && recognitionRef.current.stop(); } catch {}
    };
  }, []);

  // Persist state changes to localStorage
  useEffect(() => {
    localStorage.setItem('joe_isOpen', JSON.stringify(isOpen));
  }, [isOpen]);

  useEffect(() => {
    localStorage.setItem('joe_isMuted', JSON.stringify(isMuted));
  }, [isMuted]);

  useEffect(() => {
    localStorage.setItem('joe_showChat', JSON.stringify(showChat));
  }, [showChat]);

  // Show welcome message only ONCE ever (not on page changes)
  // This is handled by the second useEffect below - removing duplicate

  // Toggle voice input - pressing mic ALWAYS stops Joe and starts/stops listening
  const toggleListening = async () => {
    if (!recognitionRef.current) {
      setMicError('Voice input not supported in this browser');
      setTimeout(() => setMicError(null), 5000);
      return;
    }

    // Clear any previous error
    setMicError(null);

    // ALWAYS stop Joe from speaking when mic is pressed
    stopSpeaking();

    if (isListening) {
      // Already listening, stop it
      recognitionRef.current.stop();
      console.log('🎤 Stopped listening');
    } else {
      // Request mic permission first
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Stop the stream right away - we just needed to request permission
        stream.getTracks().forEach(track => track.stop());

        // Start listening
        recognitionRef.current.start();
        console.log('🎤 Started listening');
      } catch (e: any) {
        console.error('🎤 Failed to start listening:', e);
        if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
          setMicError('Microphone access denied. Please allow microphone access in your browser settings.');
        } else if (e.name === 'NotFoundError') {
          setMicError('No microphone found. Please connect a microphone and try again.');
        } else if (e.name === 'NotReadableError') {
          setMicError('Microphone is in use by another app. Please close other apps using the mic.');
        } else {
          setMicError('Could not access microphone. Please check your settings.');
        }
        setTimeout(() => setMicError(null), 5000);
      }
    }
  };

  // Set initial position based on prop (only if not already saved)
  useEffect(() => {
    const setInitialPosition = () => {
      // Only set initial position if not already initialized from localStorage
      if (positionInitialized) return;

      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      let x = 0;
      let y = windowHeight - 350; // 350px from bottom (smaller)

      switch (initialPosition) {
        case 'bottom-left':
          x = 20;
          break;
        case 'bottom-center':
          x = (windowWidth - 250) / 2;
          break;
        case 'bottom-right':
        default:
          x = windowWidth - 270;
          break;
      }

      setPosition({ x, y });
      setPositionInitialized(true);
    };

    setInitialPosition();

    // Handle window resize to keep Joe in bounds.
    // Uses clampPosition (the circle's bounds) like the other resize handler:
    // the old -250/-350 maxima are TIGHTER than the bottom-right default, so
    // they used to yank Joe out of the corner on the first resize and the
    // persist effect wrote that back. clampExpanded at paint time is what
    // keeps the 224x288 body on screen.
    const handleResize = () => {
      setPosition(prev => clampPosition(prev));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [initialPosition, positionInitialized]);

  // Persist position to localStorage when it changes
  useEffect(() => {
    if (positionInitialized && position.x >= 0 && position.y >= 0) {
      try {
        localStorage.setItem('joe_position', JSON.stringify(position));
      } catch {}
    }
  }, [position, positionInitialized]);

  // Smart frame preloading — idle frames immediately, rest when chat opens
  const framesPreloaded = useRef(false);
  useEffect(() => {
    // Always preload idle frames (1-12) — tiny subset, instant
    for (let i = 1; i <= 12; i++) {
      const img = new Image();
      img.src = getFramePath(i);
    }
  }, []);

  useEffect(() => {
    // When chat opens, preload remaining frames in background
    if (isOpen && !framesPreloaded.current) {
      framesPreloaded.current = true;
      let i = 13;
      const loadNext = () => {
        if (i > totalFrames) return;
        if (i === 50) { i++; } // skip broken frame
        const img = new Image();
        img.src = getFramePath(i);
        i++;
        // Stagger loads to avoid network congestion
        setTimeout(loadNext, 50);
      };
      loadNext();
    }
  }, [isOpen]);

  // Multi-state frame animation — each state has its own frame sequence and timing
  useEffect(() => {
    const config = animationConfigs[animationState];
    let frameIndex = 0;

    animationIntervalRef.current = setInterval(() => {
      setCurrentFrame(config.frames[frameIndex]);
      frameIndex = (frameIndex + 1) % config.frames.length;
    }, config.frameDelay);

    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationState]);

  // Idle fidget timer — plays random micro-movements every 8-15 seconds when idle
  useEffect(() => {
    if (animationState !== 'idle') return;

    const scheduleNextFidget = () => {
      const delay = 8000 + Math.random() * 7000; // 8-15 seconds
      return setTimeout(() => {
        const fidget = idleFidgets[Math.floor(Math.random() * idleFidgets.length)];
        let i = 0;
        const fidgetInterval = setInterval(() => {
          if (i >= fidget.length) {
            clearInterval(fidgetInterval);
            return;
          }
          setCurrentFrame(fidget[i]);
          i++;
        }, 120);
      }, delay);
    };

    const timer = scheduleNextFidget();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationState]);

  // Text-to-speech using Amazon Polly Neural voices (Stephen voice)
  const speakText = async (text: string, slow: boolean = false) => {
    // Use ref to get current mute state (avoid stale closure in speech recognition callback)
    if (isMutedRef.current) {
      console.log('❌ Speech muted');
      return;
    }

    // Stop any currently playing audio
    stopSpeaking();

    try {
      setAnimationState('talking');
      console.log('🎯 Calling Polly for:', text.substring(0, 50) + '...', slow ? '(slow mode)' : '');

      // Call backend Polly endpoint
      const response = await fetch('/api/joe/speak', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ text, slow })
      });

      if (!response.ok) {
        throw new Error('Failed to generate speech');
      }

      // Convert response to audio blob
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      // Create and play audio
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        console.log('✅ Polly speech ended');
        setAnimationState('idle');
        URL.revokeObjectURL(audioUrl);
      };

      audio.onerror = () => {
        console.error('❌ Audio playback error');
        setAnimationState('idle');
        URL.revokeObjectURL(audioUrl);
      };

      console.log('🎤 Playing Polly audio...');
      try {
        await audio.play();
      } catch (playError: any) {
        // Handle autoplay restriction
        if (playError.name === 'NotAllowedError') {
          console.log('⚠️ Autoplay blocked - audio will play after first click');
          // Try to play with user gesture on next interaction
          document.addEventListener('click', async function enableAudio() {
            try {
              await audio.play();
              document.removeEventListener('click', enableAudio);
            } catch (e) {
              console.error('Failed to play after click:', e);
            }
          }, { once: true });
        } else {
          throw playError;
        }
      }
    } catch (error) {
      console.error('❌ TTS error:', error);
      setAnimationState('idle');
    }
  };

  // Stop speaking
  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setAnimationState('idle');
  };

  // Check if message is a stop command
  const isStopCommand = (text: string): boolean => {
    const stopPhrases = ['stop', 'shut up', 'quiet', 'be quiet', 'hush', 'enough', 'ok stop', 'okay stop', 'stop talking', 'stop it', 'shush', 'silence', 'mute'];
    const lower = text.toLowerCase().trim();
    return stopPhrases.some(phrase => lower === phrase || lower.startsWith(phrase + ' ') || lower.endsWith(' ' + phrase));
  };

  // Send message
  const sendMessage = async () => {
    if (!input.trim()) return;

    // Check for stop command - don't send to backend, just stop speaking
    if (isStopCommand(input)) {
      stopSpeaking();
      const joeMessage: Message = {
        id: Date.now().toString(),
        type: 'joe',
        content: "Okay, I will be quiet.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, joeMessage]);
      setInput('');
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    setAnimationState('thinking');

    try {
      const response = await fetch('/api/joe/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ message: input, currentPath: location.pathname })
      });

      const data = await response.json();
      let navTo: string | null = null;
      let cleaned = (data.response || '').replace(/\[\[NAV:[^\]]+\]\]\s*/,'').trim();
      // Decode HTML entities that may have been added by input sanitizer
      cleaned = decodeHtmlEntities(cleaned);
      if (data && typeof data.response === 'string') {
        const nav = data.response.match(/\[\[NAV:([^\]]+)\]\]/);
        if (nav && nav[1]) navTo = nav[1];
      }

      const joeMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'joe',
        content: cleaned,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, joeMessage]);

      // Make Joe speak the response
      // Use speakText from response if available (for walkthroughs), otherwise use cleaned text
      // Speak slower for walkthroughs (data.source === 'walkthrough')
      const textToSpeak = data.speakText || cleaned;
      const isWalkthrough = data.source === 'walkthrough';
      console.log('🎯 Attempting to speak response...', isWalkthrough ? '(walkthrough - slow mode)' : '');
      speakText(textToSpeak, isWalkthrough);
      if (navTo) {
        setAnimationState('celebrating');
        setTimeout(() => {
          navigate(navTo!);
          setAnimationState('idle');
        }, 800);
      }

    } catch (error: any) {
      console.error('Chat error:', error);
      setAnimationState('idle');

      // Determine specific error message based on error type
      let errorContent = "Sorry, I am having trouble connecting right now. Please try again.";

      if (error?.message?.includes('NetworkError') || error?.message?.includes('Failed to fetch')) {
        errorContent = "I cannot reach the server right now. Please check your internet connection and try again.";
      } else if (error?.message?.includes('401') || error?.message?.includes('Unauthorized')) {
        errorContent = "Your session may have expired. Please refresh the page or log in again.";
      } else if (error?.message?.includes('429') || error?.message?.includes('rate limit')) {
        errorContent = "I am receiving too many requests. Please wait a moment and try again.";
      } else if (error?.message?.includes('500') || error?.message?.includes('Internal Server')) {
        errorContent = "I am experiencing technical difficulties. The team has been notified. Please try again shortly.";
      }

      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'joe',
        content: errorContent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Clear chat history
  const clearChatHistory = () => {
    setMessages([]);
    localStorage.removeItem('joe_messages');
    // Add a fresh greeting after clearing
    setTimeout(() => {
      const greeting = "Chat history cleared! How can I help you today?\n\nQuick commands:\n• \"Good morning\" - Get your daily briefing\n• \"Alerts\" - See what needs attention\n• \"Create estimate\" - Start a new estimate";
      const greetingMessage: Message = {
        id: Date.now().toString(),
        type: 'joe',
        content: greeting,
        timestamp: new Date()
      };
      setMessages([greetingMessage]);
      if (!isMuted) {
        speakText("Chat history cleared. How can I help you?");
      }
    }, 100);
  };

  // Dragging handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.no-drag')) return;

    setIsDragging(true);
    const rect = joeRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && joeRef.current) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        // Keep within window bounds. Same bounds as the resize handlers - the
        // old -250/-350 maxima excluded the bottom-right default itself, so
        // once you dragged Joe you could never drag him back to his home
        // corner. clampExpanded at paint time keeps the body on screen.
        setPosition(clampPosition({ x: newX, y: newY }));
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  // Greeting when first opened - only greet ONCE ever per session, not on every page change
  const hasGreetedRef = useRef(false);
  useEffect(() => {
    // Only greet if: Joe is open, no messages exist, AND we haven't greeted this session
    const hasGreetedStorage = localStorage.getItem('joe_hasGreeted');
    if (isOpen && messages.length === 0 && !hasGreetedRef.current && !hasGreetedStorage) {
      hasGreetedRef.current = true;
      localStorage.setItem('joe_hasGreeted', 'true');

      const greeting = "Hello! My name is Joe, your AI assistant. Ask me about jobs, schedules, estimates, or anything else!";
      setMessages([{
        id: '1',
        type: 'joe',
        content: greeting,
        timestamp: new Date()
      }]);

      // Speak greeting after a short delay
      setTimeout(() => {
        speakText(greeting);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Clamp both renders at paint time: a position persisted from an older
  // build, or from a drag before a resize, must never leave Joe off screen.
  const collapsedPos = clampPosition({
    x: position.x + COLLAPSED_OFFSET.x,
    y: position.y + COLLAPSED_OFFSET.y
  });
  const expandedPos = clampExpanded(position);

  return (
    <>
      {!isOpen && (
        /* Minimized Joe - Small Circle at persisted position */
        <div
          style={{
            position: 'fixed',
            left: `${collapsedPos.x}px`,
            top: `${collapsedPos.y}px`,
            zIndex: 1000
          }}
          className="cursor-pointer group hover:scale-110 transition-all duration-300 ease-out"
          onClick={() => setIsOpen(true)}
        >
          <div className="relative">
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-blue-500 shadow-xl bg-gray-100 transition-all duration-300">
              <img
                src={getFramePath(currentFrame)}
                alt="Joe"
                className="w-full h-full object-cover"
                style={{
                  transition: 'opacity 0.15s ease-in-out',
                  imageRendering: 'auto'
                }}
              />
            </div>
            {/* Online indicator */}
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
            {/* Talking indicator */}
            {isTalking && (
              <div className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-1.5 shadow-lg">
                <Volume2 size={14} className="text-white animate-pulse" />
              </div>
            )}
          </div>
          {/* Tooltip */}
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            Chat with Joe
          </div>
        </div>
      )}

      {isOpen && (
        /* Large Joe Avatar - Smaller size */
        <div
          ref={joeRef}
          onMouseDown={handleMouseDown}
          style={{
            position: 'fixed',
            left: `${expandedPos.x}px`,
            top: `${expandedPos.y}px`,
            zIndex: 1000,
            cursor: isDragging ? 'grabbing' : 'grab',
            transition: isDragging ? 'none' : 'opacity 0.3s ease-out'
          }}
          className={`${isDragging ? 'opacity-90' : 'opacity-100'}`}
        >
        {/* Chat Bubble (pops out above Joe) - Toggleable - Smaller to match avatar */}
        {showChat && (
          <div
            className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 w-64 no-drag"
          >
            <div className="bg-white rounded-3xl shadow-2xl border-4 border-blue-500 relative overflow-hidden">
              {/* Chat header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-3 flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2">
                  💬 Chat with Joe
                  {animationState === 'talking' && <span className="text-xs bg-blue-500 px-2 py-0.5 rounded-full animate-pulse">Speaking</span>}
                  {animationState === 'thinking' && <span className="text-xs bg-purple-500 px-2 py-0.5 rounded-full animate-pulse">Thinking...</span>}
                  {animationState === 'listening' && <span className="text-xs bg-red-500 px-2 py-0.5 rounded-full animate-pulse">Listening</span>}
                  {animationState === 'celebrating' && <span className="text-xs bg-green-500 px-2 py-0.5 rounded-full animate-pulse">Done!</span>}
                </h3>
                <button
                  onClick={clearChatHistory}
                  className="p-1 hover:bg-blue-800 rounded transition-colors"
                  title="Clear chat history"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Mic error message */}
              {micError && (
                <div className="mx-3 mt-2 p-2 bg-red-100 text-red-700 rounded-lg text-xs flex items-center gap-2">
                  <span>⚠️</span>
                  <span>{micError}</span>
                </div>
              )}

              {/* Messages area */}
              <div className="p-3 space-y-2 bg-gray-50 max-h-40 overflow-y-auto">
                {messages.length === 0 && (
                  <div className="text-center text-gray-500 text-sm py-4">
                    👋 Say hello to Joe or ask a question!
                  </div>
                )}
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`${
                      msg.type === 'user'
                        ? 'bg-blue-100 ml-8 text-right'
                        : 'bg-white mr-8'
                    } p-2 rounded-lg text-sm shadow-sm`}
                  >
                    <p className="text-gray-800">{msg.content}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatTimeOfDay(msg.timestamp)}
                    </p>
                  </div>
                ))}
                {isTyping && (
                  <div className="bg-white mr-8 p-2 rounded-lg text-sm shadow-sm">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Input area */}
              <div className="p-3 border-t bg-white rounded-b-xl">
                <div className="flex gap-1.5">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={isListening ? "Listening..." : "Type or use mic..."}
                    className="flex-1 px-2.5 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-w-0"
                    disabled={isTyping || isListening}
                  />
                  <button
                    onClick={toggleListening}
                    disabled={isTyping}
                    className={`px-2 py-2 rounded-lg transition-all flex-shrink-0 ${
                      isListening
                        ? 'bg-red-600 text-white animate-pulse hover:bg-red-700'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    } disabled:bg-gray-300 disabled:cursor-not-allowed`}
                    title={isListening ? "Stop listening" : "Start voice input"}
                  >
                    {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                  </button>
                  <button
                    onClick={sendMessage}
                    disabled={!input.trim() || isTyping}
                    className="px-2 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </div>

              {/* Speech bubble tail - larger and more prominent */}
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[16px] border-transparent border-t-blue-500"></div>
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[10px] border-r-[10px] border-t-[14px] border-transparent border-t-white"></div>
            </div>
          </div>
        )}

        {/* Joe's Avatar - Smaller size */}
        <div className="relative">
          {/* Animated Joe - TRANSPARENT BACKGROUND - Reduced size */}
          <div className="w-56 h-72 relative">
            <img
              src={getFramePath(currentFrame)}
              alt="Joe"
              className="w-full h-full object-contain drop-shadow-2xl"
              style={{
                filter: animationState === 'talking'
                  ? 'brightness(1.1) drop-shadow(0 0 15px rgba(59, 130, 246, 0.5))'
                  : animationState === 'thinking'
                  ? 'brightness(1.05) drop-shadow(0 0 10px rgba(168, 85, 247, 0.4))'
                  : animationState === 'celebrating'
                  ? 'brightness(1.15) drop-shadow(0 0 15px rgba(34, 197, 94, 0.5))'
                  : animationState === 'listening'
                  ? 'brightness(1.05) drop-shadow(0 0 10px rgba(239, 68, 68, 0.4))'
                  : 'brightness(1)',
                transition: 'filter 0.2s ease-out, opacity 0.15s ease-in-out',
                imageRendering: 'auto'
              }}
            />

            {/* Status indicators overlay */}
            <div className="absolute top-3 right-3 flex flex-col gap-2">
              {/* Online indicator */}
              <div className="bg-green-500 rounded-full p-2 shadow-lg animate-pulse">
                <div className="w-3 h-3 bg-white rounded-full"></div>
              </div>

              {/* Talking indicator */}
              {isTalking && (
                <div className="bg-blue-500 rounded-full p-2 shadow-lg animate-pulse">
                  <Volume2 size={16} className="text-white" />
                </div>
              )}

              {/* Listening indicator */}
              {isListening && (
                <div className="bg-red-500 rounded-full p-2 shadow-lg animate-pulse">
                  <Mic size={16} className="text-white" />
                </div>
              )}
            </div>

            {/* Drag handle */}
            <div className="absolute top-3 left-3 bg-white bg-opacity-70 rounded-full p-2 cursor-grab hover:bg-opacity-100 transition-all">
              <Move size={16} className="text-gray-600" />
            </div>
          </div>

          {/* Control buttons */}
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2 no-drag">
            <button
              onClick={() => setShowChat(!showChat)}
              className={`p-2 rounded-full shadow-lg transition-colors ${
                showChat
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              title={showChat ? "Hide chat" : "Show chat"}
            >
              <MessageCircle size={20} />
            </button>

            <button
              onClick={toggleListening}
              disabled={isTyping}
              className={`p-2 rounded-full shadow-lg transition-all ${
                isListening
                  ? 'bg-red-600 text-white animate-pulse hover:bg-red-700'
                  : 'bg-green-600 text-white hover:bg-green-700'
              } disabled:bg-gray-300 disabled:cursor-not-allowed`}
              title={isListening ? "Stop listening" : "Start voice input"}
            >
              {isListening ? <MicOff size={20} /> : <Mic size={20} />}
            </button>

            <button
              onClick={() => {
                setIsMuted(!isMuted);
                if (!isMuted) stopSpeaking();
              }}
              className="bg-white text-gray-700 p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
              title={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>

            <button
              onClick={() => {
                stopSpeaking();
                setIsOpen(false);
              }}
              className="bg-white text-gray-700 p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
              title="Close Joe"
            >
              <X size={20} />
            </button>
          </div>

          {/* Name badge */}
          <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full shadow-lg font-bold text-lg">
            Joe {isTalking && '🎤'}
          </div>
        </div>
        </div>
      )}
    </>
  );
};

export default JoeLargeAvatar;
